import { useState, useEffect } from 'react';
import { authService } from '../services';
import { useAuth } from '../context/AuthContextOIDC';
import { requestCache, CACHE_KEYS } from '../services/requestCache';

interface UserRole {
  role: 'admin' | 'reader' | 'qs' | 'super_qs' | 'unauthorized' | 'loading';
  permissions: string[];
  user: {
    name: string;
    email: string;
    organization: {
      id: string;
      name: string;
      email: string;
      status: string;
    } | null;
  } | null;
  isLoading: boolean;
  error: string | null;
  isAdminInForeignOrganization: boolean;
}

export const useUserRole = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [userRole, setUserRole] = useState<UserRole>({
    role: 'loading',
    permissions: [],
    user: null,
    isLoading: true,
    error: null,
    isAdminInForeignOrganization: false
  });

  useEffect(() => {
    const fetchUserRole = async () => {
      // Warte bis Auth-Loading fertig ist
      if (authLoading) {
        return;
      }

      // Nur API-Call machen wenn Benutzer authentifiziert ist
      if (!isAuthenticated) {
        setUserRole({
          role: 'unauthorized',
          permissions: [],
          user: null,
          isLoading: false,
          error: null,
          isAdminInForeignOrganization: false
        });
        return;
      }

      try {
        const data = await requestCache.cachedCall(
          CACHE_KEYS.USER_ROLE,
          () => authService.getUserRole(),
          60000 // 60 Sekunden Cache für User Role
        );
        
        
        // Prüfe ob Admin in fremder Organisation ist
        const isAdminInForeignOrg = data.role === 'admin' && 
          !!data.user?.organization?.name && 
          !data.user.organization.name.includes('SmartCheck System Administration') &&
          !data.user.organization.name.includes('DORAsmartCheck System Administration');
        
        setUserRole({
          role: data.role,
          permissions: data.permissions,
          user: data.user,
          isLoading: false,
          error: null,
          isAdminInForeignOrganization: isAdminInForeignOrg
        });
      } catch (error) {
        console.error('❌ Error fetching user role:', error);
        setUserRole({
          role: 'unauthorized',
          permissions: [],
          user: null,
          isLoading: false,
          error: 'Fehler beim Laden der Benutzerrolle',
          isAdminInForeignOrganization: false
        });
      }
    };

    fetchUserRole();
  }, [isAuthenticated, authLoading]);

  return userRole;
}; 